import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import DashboardLayout from '../../components/DashboardLayout';
import api from '../../utils/api';
import { Gift, Search, Trophy, Pencil, Plus, Trash2, Save, X, Loader2 } from 'lucide-react';

export default function AdminReward() {
  const [rewardLevels, setRewardLevels] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);

  // Edit mode state — a separate working copy so the live table isn't
  // touched until admin hits Save, and Cancel can just discard it.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState([]);
  const [saving, setSaving] = useState(false);

  const [achievements, setAchievements] = useState([]);
  const [total, setTotal] = useState(0);
  const [grandTotal, setGrandTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const LIMIT = 20;

  const fetchRewardConfig = useCallback(() => {
    setConfigLoading(true);
    return api.get('/settings/income-plan-config')
      .then(r => setRewardLevels(r.data.rewardLevels || []))
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load reward plan config'))
      .finally(() => setConfigLoading(false));
  }, []);

  useEffect(() => { fetchRewardConfig(); }, [fetchRewardConfig]);

  const fetchAchievements = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ page, limit: LIMIT, type: 'reward' });
    if (search) params.append('search', search);
    api.get(`/income/admin/all?${params}`)
      .then(r => {
        setAchievements(r.data.incomes);
        setTotal(r.data.total);
        setGrandTotal(r.data.totalsByType?.find(t => t._id === 'reward')?.total || 0);
      })
      .catch(err => toast.error(err.response?.data?.message || 'Failed to load reward achievements'))
      .finally(() => setLoading(false));
  }, [page, search]);

  useEffect(() => { fetchAchievements(); }, [fetchAchievements]);

  const formatCurrency = (n) => `₹${(n || 0).toLocaleString('en-IN')}`;
  const totalPages = Math.ceil(total / LIMIT);

  // ---------- Edit mode helpers ----------

  const startEditing = () => {
    setDraft(rewardLevels.map(rl => ({ ...rl })));
    setEditing(true);
  };

  const cancelEditing = () => {
    setEditing(false);
    setDraft([]);
  };

  const updateDraftField = (index, field, value) => {
    setDraft(prev => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  };

  const addRow = () => {
    const nextLevel = draft.length ? Math.max(...draft.map(r => Number(r.level) || 0)) + 1 : 1;
    setDraft(prev => [...prev, { level: nextLevel, business: '', reward: '' }]);
  };

  const removeRow = (index) => {
    setDraft(prev => prev.filter((_, i) => i !== index));
  };

  const saveDraft = async () => {
    if (draft.length === 0) {
      return toast.error('At least one reward slab is required');
    }

    // Basic client-side validation before hitting the API — server
    // re-validates everything anyway, this is just to fail fast.
    const cleaned = [];
    for (let i = 0; i < draft.length; i++) {
      const level = Number(draft[i].level);
      const business = Number(draft[i].business);
      const reward = Number(draft[i].reward);
      if (!Number.isInteger(level) || level < 1) {
        return toast.error(`Row ${i + 1}: Level must be a whole number of at least 1`);
      }
      if (isNaN(business) || business < 0) {
        return toast.error(`Row ${i + 1}: Business target must be a valid non-negative number`);
      }
      if (isNaN(reward) || reward < 0) {
        return toast.error(`Row ${i + 1}: Reward amount must be a valid non-negative number`);
      }
      cleaned.push({ level, business, reward });
    }

    setSaving(true);
    try {
      const res = await api.put('/settings/reward-levels', { rewardLevels: cleaned });
      toast.success(res.data.message || 'Reward slabs updated');
      setRewardLevels(res.data.rewardLevels || cleaned);
      setEditing(false);
      setDraft([]);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update reward slabs');
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout>
      <h1 className="page-title">Reward Management</h1>
      <p className="page-subtitle">Milestone reward slabs and every user who has achieved a reward so far</p>

      <div className="wallet-grid" style={{ marginBottom: 24 }}>
        <div className="wallet-card">
          <div style={{ marginBottom: 6, color: '#f59e0b', display: 'flex', justifyContent: 'center' }}><Gift size={24} /></div>
          <div className="wallet-label">Total Reward Paid</div>
          <div className="wallet-value" style={{ color: '#f59e0b' }}>{formatCurrency(grandTotal)}</div>
        </div>
        <div className="wallet-card">
          <div style={{ marginBottom: 6, color: '#a855f7', display: 'flex', justifyContent: 'center' }}><Trophy size={24} /></div>
          <div className="wallet-label">Total Achievements</div>
          <div className="wallet-value" style={{ color: '#a855f7' }}>{total}</div>
        </div>
        <div className="wallet-card">
          <div style={{ marginBottom: 6, color: '#22c55e', display: 'flex', justifyContent: 'center' }}><Gift size={24} /></div>
          <div className="wallet-label">Reward Slabs Defined</div>
          <div className="wallet-value" style={{ color: '#22c55e' }}>{rewardLevels.length}</div>
        </div>
      </div>

      {/* Reward Slab Table — editable */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Gift size={18} /> Reward Slabs (Plan)</h3>
          {!configLoading && !editing && (
            <button type="button" className="btn btn-outline btn-sm" onClick={startEditing}>
              <Pencil size={14} /> Edit Slabs
            </button>
          )}
          {editing && (
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" className="btn btn-outline btn-sm" onClick={cancelEditing} disabled={saving}>
                <X size={14} /> Cancel
              </button>
              <button type="button" className="btn btn-primary btn-sm" onClick={saveDraft} disabled={saving}>
                {saving ? <Loader2 size={14} className="spin" /> : <Save size={14} />} Save Changes
              </button>
            </div>
          )}
        </div>

        {configLoading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : !editing ? (
          <div className="table-wrap">
            <table className="tp-table">
              <thead><tr><th>Level</th><th>Business Target</th><th>Reward Amount</th></tr></thead>
              <tbody>
                {rewardLevels.map(rl => (
                  <tr key={rl.level}>
                    <td>Level {rl.level}</td>
                    <td>{formatCurrency(rl.business)}</td>
                    <td style={{ color: '#f59e0b', fontWeight: 700 }}>{formatCurrency(rl.reward)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead><tr><th style={{ width: 110 }}>Level</th><th>Business Target (₹)</th><th>Reward Amount (₹)</th><th style={{ width: 50 }}></th></tr></thead>
              <tbody>
                {draft.map((rl, i) => (
                  <tr key={i}>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        style={{ padding: '8px 10px' }}
                        min={1}
                        value={rl.level}
                        onChange={e => updateDraftField(i, 'level', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        style={{ padding: '8px 10px' }}
                        min={0}
                        value={rl.business}
                        onChange={e => updateDraftField(i, 'business', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="form-control"
                        style={{ padding: '8px 10px' }}
                        min={0}
                        value={rl.reward}
                        onChange={e => updateDraftField(i, 'reward', e.target.value)}
                      />
                    </td>
                    <td>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        style={{ padding: '6px 10px' }}
                        onClick={() => removeRow(i)}
                        title="Remove slab"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 14 }} onClick={addRow}>
              <Plus size={14} /> Add Slab
            </button>
          </div>
        )}

        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 10 }}>
          Reward slabs are one-time milestone bonuses credited automatically the moment a user's team business
          crosses a target. Levels must be unique and business targets must increase with each level. Changes
          apply immediately to future achievements — past reward credits already given to users are not affected.
        </div>
      </div>

      {/* Achievements Log */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}><Trophy size={18} /> Reward Achievements</h3>
          <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>Total: {total} records</div>
        </div>

        <div className="form-group" style={{ maxWidth: 320 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Search size={14} /> Search User</label>
          <input
            type="text"
            className="form-control"
            placeholder="Name, email, or referral code..."
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {loading ? (
          <div className="loader-wrap"><div className="loader"></div></div>
        ) : achievements.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon"><Gift size={40} /></div>
            <h3>No reward achievements yet</h3>
          </div>
        ) : (
          <div className="table-wrap">
            <table className="tp-table">
              <thead><tr><th>Date</th><th>User</th><th>Description</th><th>Amount</th></tr></thead>
              <tbody>
                {achievements.map(inc => (
                  <tr key={inc._id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: '0.82rem' }}>{new Date(inc.createdAt).toLocaleDateString('en-IN')}</td>
                    <td>
                      <div style={{ fontWeight: 600 }}>{inc.user?.name || '—'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{inc.user?.referralCode}</div>
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{inc.description || '—'}</td>
                    <td style={{ color: '#f59e0b', fontWeight: 700 }}>+{formatCurrency(inc.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totalPages > 1 && (
          <div className="pagination">
            <button className="page-btn" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            {[...Array(Math.min(5, totalPages))].map((_, i) => {
              const p = i + Math.max(1, page - 2);
              if (p > totalPages) return null;
              return <button key={p} className={`page-btn ${page === p ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>;
            })}
            <button className="page-btn" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}